import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { Checkbox } from '../ui/checkbox';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import {
    ALL_GROUPS,
    PERMISSION_GROUP,
    type AccessRow,
} from './actorAccessItems';
import { updateActor, type ActorWithCount } from '../../services/api/actorService';
import { getActorDisplayName } from '../../utils/roleUtils';
import { useToast } from '../../hooks/use-toast';

interface ActorMatrixProps {
    actors: ActorWithCount[];
    /** manageEndUsers-only callers: only the permission rows are shown */
    limited?: boolean;
    onActorsChange: (actors: ActorWithCount[]) => void;
    onEdit: (actor: ActorWithCount) => void;
}

export default function ActorMatrix({
    actors,
    limited = false,
    onActorsChange,
    onEdit,
}: ActorMatrixProps) {
    const { toast } = useToast();
    const [saving, setSaving] = useState<string | null>(null);
    const groups = limited ? [PERMISSION_GROUP] : ALL_GROUPS;

    const hasAccess = (actor: ActorWithCount, row: AccessRow) =>
        row.type === 'Right'
            ? actor.rights.includes(row.key)
            : actor.deptPermissions.includes(row.key);

    const toggle = async (actor: ActorWithCount, row: AccessRow) => {
        const cellKey = `${actor._id}-${row.key}`;
        if (saving) return;

        const checked = hasAccess(actor, row);
        let newRights = [...actor.rights];
        let newPerms = [...actor.deptPermissions];

        if (row.type === 'Right') {
            newRights = checked
                ? newRights.filter((r) => r !== row.key)
                : [...newRights, row.key];
        } else {
            newPerms = checked
                ? newPerms.filter((p) => p !== row.key)
                : [...newPerms, row.key];
        }

        const optimistic = actors.map((a) =>
            a._id === actor._id
                ? { ...a, rights: newRights, deptPermissions: newPerms }
                : a
        );
        onActorsChange(optimistic);

        setSaving(cellKey);
        try {
            await updateActor(
                actor._id,
                row.type === 'Right'
                    ? { rights: newRights }
                    : { deptPermissions: newPerms }
            );
        } catch {
            onActorsChange(actors);
            toast({ title: 'Failed to update actor', variant: 'destructive' });
        } finally {
            setSaving(null);
        }
    };

    return (
        <TooltipProvider delayDuration={200}>
            <div className="rounded-lg border border-subtle overflow-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-subtle bg-muted/40">
                            <th className="sticky left-0 z-20 min-w-[200px] bg-muted/40 px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                                Access Item
                            </th>
                            {actors.map((actor) => (
                                <th
                                    key={actor._id}
                                    className="min-w-[110px] px-3 py-2 text-center text-xs font-medium text-foreground"
                                >
                                    <div className="flex items-center justify-center gap-1">
                                        <span>{getActorDisplayName(actor)}</span>
                                        <button
                                            type="button"
                                            onClick={() => onEdit(actor)}
                                            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
                                            aria-label={`Edit ${getActorDisplayName(actor)}`}
                                        >
                                            <Pencil className="h-3 w-3" />
                                        </button>
                                    </div>
                                    <div className="mt-0.5 font-normal text-muted-foreground">
                                        {actor.userCount} user
                                        {actor.userCount !== 1 ? 's' : ''}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((topGroup) => (
                            <>
                                <tr
                                    key={`__top-${topGroup.group}`}
                                    className="border-t-2 border-subtle"
                                >
                                    <td
                                        colSpan={1 + actors.length}
                                        className="sticky left-0 bg-muted/40 px-3 py-2 text-xs font-bold uppercase tracking-widest text-foreground"
                                    >
                                        {topGroup.group}
                                    </td>
                                </tr>
                                {topGroup.subgroups.map((sub) => (
                                    <>
                                        <tr
                                            key={`__sub-${topGroup.group}-${sub.label}`}
                                            className="border-t border-subtle"
                                        >
                                            <td
                                                colSpan={1 + actors.length}
                                                className="sticky left-0 bg-muted/20 px-3 py-1 pl-5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
                                            >
                                                {sub.label}
                                            </td>
                                        </tr>
                                        {sub.rows.map((row) => (
                                            <tr
                                                key={row.key}
                                                className="border-b border-subtle last:border-b-0 hover:bg-muted/20"
                                            >
                                                <td className="sticky left-0 z-10 bg-background px-3 py-2 pl-7 font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="cursor-default">
                                                                    {row.label}
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="right">
                                                                {row.description}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                        <span
                                                            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                                                                row.access === 'write'
                                                                    ? 'bg-amber-500/15 text-amber-400'
                                                                    : 'bg-sky-500/15 text-sky-400'
                                                            }`}
                                                        >
                                                            {row.access === 'write'
                                                                ? 'Write'
                                                                : 'Read'}
                                                        </span>
                                                    </div>
                                                </td>
                                                {actors.map((actor) => (
                                                    <td
                                                        key={actor._id}
                                                        className="px-3 py-2 text-center"
                                                    >
                                                        <Checkbox
                                                            checked={hasAccess(
                                                                actor,
                                                                row
                                                            )}
                                                            onCheckedChange={() =>
                                                                toggle(actor, row)
                                                            }
                                                            disabled={
                                                                saving ===
                                                                `${actor._id}-${row.key}`
                                                            }
                                                            className="mx-auto"
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </>
                                ))}
                            </>
                        ))}
                    </tbody>
                </table>
            </div>
        </TooltipProvider>
    );
}
